import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import "../styles/createCoupon.css";
import couponMainActions from "../components/modules/couponMainActions";
import { ReactComponent as CloseIcon } from "../assets/images/common/closeIcon.svg";
import MetaButton from "../components/Buttons/SubmitButton";
import PhoneNumberField from "../components/Fields/PhoneNumberField";
import QuestionsApi from "../api/questions-api";
import { getLanguage, strings } from "../utils/localization";
import { ReactComponent as MessageIcon } from "./assets/message-icon.svg";
import AcceptTermsFooter from "../v2/components/Footer/AcceptTermsFooter";
import useAnalytics from "../utils/useAnalytics";
import { validatePhoneNumber } from "../utils/validation";
import { useLocation, useParams } from "react-router-dom";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
  height: 100vh;
`;

const Wrapper = styled.div`
  position: relative;
  background-color: ${(props) => props.backgroundColor};
  border-radius: 40px 40px 0 0;
  padding: 10px;
  bottom: 0;
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 1000;
  overscroll-behavior: contain;
  touch-action: none;
  animation: MoveUpDown 0.3s linear;
  font-family: Arial, sans-serif;

  @keyframes MoveUpDown {
    0% {
      transform: translateY(1000px);
    }
    100% {
      transform: translateY(0);
    }
  }
`;

const CloseButton = styled.div`
  padding: 10px;
  cursor: pointer;
  right: 15px;
  top: 10px;
  position: absolute;
  z-index: 1;
`;

const LogoContainer = styled.div`
  margin: 45px 0 29px 0;
  width: 189px;

  img {
    width: 100%;
    height: 118px;
    object-fit: contain;
  }
`;

const TitleText = styled.div`
  color: ${(props) => props.color};
  font-size: 23px;
  font-weight: bold;
  line-height: 22px;
  letter-spacing: 0.86px;
  white-space: normal;
  word-break: break-word;
  width: 100%;
  max-width: 98%;
  margin: 0 auto;
`;

const DescriptionText = styled.div`
  color: ${(props) => props.color};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: normal;
  word-break: break-word;
  width: 100%;
  max-width: 90%;
  margin: 11px auto 0;
`;

const ThankYouContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 87px;
  width: 325px;
  animation: fadeIn 1s ease forwards;
  margin: auto 0;
  padding: 23px 3px 0;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ThankYouText = styled.p`
  margin: 0 0 3px 5px;
  color: ${(props) => props.color};
  font-size: 14px;
  font-weight: 600;
`;

const FormContainer = styled.div`
  width: 325px;
  margin: 23px auto 0;
  padding: 0 3px;
  margin-top: auto;

  .matadot-live-chat-123789-write-text-message {
    padding: 10px;
    position: relative;
    padding-bottom: 0;

    /* Phone input field container */
    .react-tel-input {
      position: relative;

      .form-control {
        height: 54px;
        border: none;
        background-color: #f4f7fc !important;
        border-radius: 100px;
        padding-right: 110px !important;
        padding-left: 50px !important;
        width: 100%;
        font-size: 14px;
        box-shadow: none;
        margin: 0 !important;
        box-sizing: border-box;

        &:focus {
          outline: none;
        }
      }

      .flag-dropdown {
        background: transparent;
        border: none;
        border-radius: 100px 0 0 100px;

        &.open {
          background: transparent;

          .selected-flag {
            background: transparent;
          }
        }

        .selected-flag {
          background: transparent;
          border-radius: 100px 0 0 100px;
          padding: 0 0 0 15px;

          &:hover,
          &:focus {
            background: transparent;
          }
        }
      }
    }

    /* Submit button styling */
    button {
      position: absolute;
      top: 17px;
      right: 17px;
      height: 40px;
      border-radius: 100px;
      color: #fff;
      padding: 0 20px;
      border: none;
      font-weight: bold;
      white-space: nowrap;
      cursor: pointer;
      transition: opacity 0.3s ease;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &:hover:not(:disabled) {
        opacity: 0.9;
      }
    }
  }
`;

const FooterWrapper = styled.div`
  margin-top: 30px;
  margin-bottom: 5px;
`;

const getLocalizedText = (items, language, fallbackLanguage = "en") => {
  return (
    items?.find((x) => x.language === language)?.text ||
    items?.find((x) => x.language === fallbackLanguage)?.text
  );
};

const DesktopCoupon = () => {
  const { token: locationId, lang } = useParams();
  const [language, setLanguage] = useState("en");
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [couponData, setCouponData] = useState();
  const [contentVisible, setContentVisible] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState();
  const [parentURL, setParentURL] = useState();

  const {
    sendCommSubmissionEvent,
    sendCommEngagementEvent,
    sendCtaInteractionEvent,
  } = useAnalytics(couponData?.GATrackingNumber);
  const location = useLocation();

  const setLanguageValue = useCallback(() => {
    let languageValue = lang || "en";
    if (languageValue === "es") languageValue = "sp";

    setLanguage(languageValue);
    strings.setLanguage(languageValue === "sp" ? "es" : languageValue);
  }, [lang]);

  useEffect(() => {
    if (couponData) {
      sendCommEngagementEvent({ widget: "Desktop Coupon", status: "load" });
      setContentVisible(true);
    }
    setLanguageValue();
  }, [couponData, sendCommEngagementEvent, setLanguageValue]);

  useEffect(() => {
    const fetchCouponData = async () => {
      if (!locationId) return;

      const params = new URLSearchParams(location.search);
      const urlValue = params.get("url");

      const result = await couponMainActions.getCoupon(locationId);
      if (result.success) {
        setCouponData(result.data);
        setParentURL(location.hash ? urlValue + location.hash : urlValue);
      }
    };

    fetchCouponData();
  }, [locationId, location]);

  const handlePhoneNumberPaste = (event) => {
    let pasteValue = (event.clipboardData || window.clipboardData)
      .getData("text")
      .replace(/[-\s()]/g, "")
      .replace("tel:", "")
      .replace("+", "");

    if (couponData?.organization_country_code?.toLowerCase() === "au") {
      if (pasteValue.length === 9) pasteValue = `0${pasteValue}`;
      if (pasteValue.length >= 11) pasteValue = `0${pasteValue.slice(2)}`;
    } else {
      if (pasteValue.length === 10) pasteValue = `1${pasteValue}`;
      if (pasteValue.length === 11) pasteValue = `1${pasteValue.slice(1)}`;
    }

    setPhoneNumber(pasteValue);
  };

  const handleSubmit = async () => {
    sendCommSubmissionEvent({ widget: "Desktop Coupon" });

    const param = {
      phone_number: phoneNumber,
      firstName: "",
      link: parentURL,
      question: "Desktop coupon",
      language: getLanguage(language),
    };

    await QuestionsApi.desktopCoupon({
      locationId: couponData._location_id,
      param,
    });
    setFormSubmitted(true);
  };

  const handleClose = () => {
    window.top.postMessage("matador_desktop_coupon_message_hidden", "*");
    setContentVisible(false);
  };

  if (!couponData?.showOnDesktop || !contentVisible) return null;

  const title = getLocalizedText(couponData.title, language);
  const description = getLocalizedText(couponData.description, language);

  return (
    <Container
      onClick={() => sendCtaInteractionEvent({ widget: "Desktop Coupon" })}
    >
      <Wrapper backgroundColor={couponData.primaryBackgroundColor}>
        <CloseButton onClick={handleClose}>
          <CloseIcon stroke={couponData.titleColor} />
        </CloseButton>

        <LogoContainer>
          {couponData.logoImageUrl && (
            <img alt="Cover" src={couponData.logoImageUrl} />
          )}
        </LogoContainer>

        {title && <TitleText color={couponData.titleColor}>{title}</TitleText>}

        {description && (
          <DescriptionText color={couponData.descriptionColor}>
            {description}
          </DescriptionText>
        )}

        {formSubmitted ? (
          <ThankYouContainer>
            <MessageIcon height={18} width={23} fill={couponData.titleColor} />
            <ThankYouText color={couponData.titleColor}>
              {strings.CTA_THANK_YOU}
            </ThankYouText>
          </ThankYouContainer>
        ) : (
          <FormContainer>
            <div className="matadot-live-chat-123789-write-text-message">
              <PhoneNumberField
                value={phoneNumber}
                onChange={setPhoneNumber}
                defaultCountryFlag={
                  couponData?.organizationCountryCode?.toLowerCase() || "us"
                }
                getPhoneNumberFromClipboard={handlePhoneNumberPaste}
              />
              <MetaButton
                style={{
                  backgroundColor: couponData.backgroundColor,
                  position: "absolute",
                  top: "17px",
                  right: "17px",
                  height: "40px",
                  borderRadius: "100px",
                  padding: "0 20px",
                }}
                onClick={handleSubmit}
                disabled={!validatePhoneNumber(phoneNumber)}
              />
            </div>
            <FooterWrapper>
              <AcceptTermsFooter
                color={couponData.titleColor}
                termsLink={couponData.termsConditionsLink}
                paddingLeft={10}
              />
            </FooterWrapper>
          </FormContainer>
        )}
      </Wrapper>
    </Container>
  );
};

export default React.memo(DesktopCoupon);
